import './style.css';
import { NPC_PLAYER_ID, npcMovement } from './game/npc';
import { addPlayer, createWorld, restartWorld, sideForPlayer, startWorld, stepPaddle, stepWorld, type MoveAction, type Side } from './game/world';
import { KeyboardInput } from './input/keyboard';
import { PeerSession } from './net/peerSession';
import type { GameSnapshot } from './net/protocol';
import { GameRenderer } from './render/gameRenderer';
import { createAppUi } from './ui/appUi';

const root = document.querySelector<HTMLElement>('#app');
if (!root) throw new Error('Missing #app root');

const ui = createAppUi(root);
const world = createWorld();
const input = new KeyboardInput(ui.moveLeftButton, ui.moveRightButton);
const renderer = new GameRenderer(ui.canvasHost);
const session = new PeerSession();
const remoteInputs = new Map<string, MoveAction>();
input.start();

let role: 'idle' | 'host' | 'client' = 'idle';
let localId = '';
let localSide: Side | null = null;
let sequence = 0;

session.onEvent((event) => {
  if (event.type === 'status') ui.status.textContent = event.text;
  if (event.type === 'welcome') {
    if (role === 'client') {
      localId = event.playerId;
      localSide = event.side;
      addPlayer(world, localId, localSide);
    } else if (role === 'host') {
      world.paddles.delete(NPC_PLAYER_ID);
      addPlayer(world, event.playerId, event.side);
      restartWorld(world);
      session.broadcastSnapshot(toSnapshot());
      ui.status.textContent = 'Player 2 joined — press Start';
    }
  }
  if (event.type === 'input') remoteInputs.set(event.playerId, event.move);
  if (event.type === 'restart' && role === 'host' && world.winner) restartMatch();
  if (event.type === 'start' && role === 'host' && !world.started) startMatch();
  if (event.type === 'leave') {
    remoteInputs.delete(event.playerId);
    world.paddles.delete(event.playerId);
    addNpc();
    ui.status.textContent = 'Rival left — playing NPC while waiting';
  }
  if (event.type === 'snapshot' && role === 'client') applySnapshot(event);
});

ui.createButton.addEventListener('click', async () => {
  resetSessionState();
  ui.setBusy(true);
  ui.status.textContent = 'Creating room…';
  try {
    localId = await session.create();
    role = 'host';
    localSide = 'south';
    addPlayer(world, localId, localSide);
    addNpc();
    ui.roomInput.value = localId;
    const inviteUrl = makeInviteUrl(localId);
    window.history.replaceState(null, '', inviteUrl);
    ui.copyInviteButton.hidden = false;
    ui.shareInviteButton.hidden = typeof navigator.share !== 'function';
    ui.status.textContent = 'NPC ready — press Start while waiting for a rival';
  } catch (error) {
    ui.status.textContent = error instanceof Error ? error.message : 'Could not create room';
    ui.setBusy(false);
  }
});

ui.joinForm.addEventListener('submit', (event) => {
  event.preventDefault();
  void joinRoom(ui.roomInput.value.trim());
});

ui.copyInviteButton.addEventListener('click', async () => {
  try {
    await navigator.clipboard.writeText(makeInviteUrl(localId));
    ui.status.textContent = 'Invite link copied — send it to your rival';
  } catch {
    ui.status.textContent = 'Copy blocked — copy URL from address bar';
  }
});

ui.shareInviteButton.addEventListener('click', async () => {
  try {
    await navigator.share({ title: 'Blind Pong', text: 'Join my Blind Pong match', url: makeInviteUrl(localId) });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return;
    ui.status.textContent = 'Could not share invite — use Copy invite link';
  }
});

ui.startButton.addEventListener('click', () => {
  if (role === 'host') startMatch();
  else if (role === 'client') {
    session.sendStart();
    ui.status.textContent = 'Start requested';
  }
});

ui.restartButton.addEventListener('click', () => {
  if (role === 'host') restartMatch();
  else if (role === 'client') {
    session.sendRestart();
    ui.status.textContent = 'Restart requested';
  }
});

ui.deleteRoomButton.addEventListener('click', () => {
  session.close();
  role = 'idle';
  resetSessionState();
  ui.setBusy(false);
  ui.roomInput.value = '';
  const url = new URL(window.location.href);
  url.searchParams.delete('room');
  window.history.replaceState(null, '', url);
  ui.status.textContent = 'Room deleted';
});

async function joinRoom(roomId: string): Promise<void> {
  if (!roomId || roomId.length > 64) {
    ui.status.textContent = 'Invalid room link';
    return;
  }
  resetSessionState();
  ui.roomInput.value = roomId;
  ui.setBusy(true);
  ui.status.textContent = 'Joining room…';
  try {
    role = 'client';
    await session.join(roomId);
  } catch (error) {
    role = 'idle';
    ui.status.textContent = error instanceof Error ? error.message : 'Could not join room';
    ui.setBusy(false);
  }
}

function addNpc(): void {
  addPlayer(world, NPC_PLAYER_ID, 'north');
}

function restartMatch(): void {
  if (!world.winner) return;
  restartWorld(world);
  ui.status.textContent = 'Match reset — press Start';
  if (role === 'host') session.broadcastSnapshot(toSnapshot());
}

function startMatch(): void {
  if (world.started || world.paddles.size < 2) return;
  startWorld(world);
  ui.status.textContent = 'Match started';
  if (role === 'host') session.broadcastSnapshot(toSnapshot());
}

function makeInviteUrl(roomId: string): string {
  const url = new URL(window.location.href);
  url.searchParams.set('room', roomId);
  url.hash = '';
  return url.toString();
}

const TICK_SECONDS = 1 / 60;
let previousTime = performance.now();
let accumulator = 0;
let animationFrame = 0;

function frame(time: number): void {
  accumulator += Math.min((time - previousTime) / 1000, 0.1);
  previousTime = time;
  while (accumulator >= TICK_SECONDS) {
    if (role === 'host') {
      const local = world.paddles.get(localId);
      if (local) stepPaddle(local, localMovement(), TICK_SECONDS);
      for (const [id, move] of remoteInputs) {
        const remote = world.paddles.get(id);
        if (remote) stepPaddle(remote, move, TICK_SECONDS);
      }
      const npc = world.paddles.get(NPC_PLAYER_ID);
      if (npc) stepPaddle(npc, npcMovement(world, npc), TICK_SECONDS);
      stepWorld(world, TICK_SECONDS);
      if (world.tick % 3 === 0) session.broadcastSnapshot(toSnapshot());
    } else if (role === 'client') {
      if (world.tick % 3 === 0) session.sendInput(sequence++, localMovement());
      world.tick += 1;
    }
    accumulator -= TICK_SECONDS;
  }

  updateHud();
  renderer.render(world, localId);
  animationFrame = requestAnimationFrame(frame);
}

function localMovement(): MoveAction {
  const move = input.movement();
  return { x: localSide === 'north' ? -move.x : move.x };
}

function applySnapshot(snapshot: GameSnapshot): void {
  world.tick = snapshot.tick;
  world.hitCount = snapshot.hitCount;
  world.started = snapshot.started;
  world.winner = snapshot.winner;
  Object.assign(world.ball, snapshot.ball);
  world.paddles.clear();
  for (const paddle of snapshot.paddles) {
    const added = addPlayer(world, paddle.id, paddle.side);
    if (added) Object.assign(added, paddle);
  }
  localSide = sideForPlayer(world, localId);
}

function toSnapshot(): GameSnapshot {
  return { tick: world.tick, hitCount: world.hitCount, started: world.started, paddles: [...world.paddles.values()], ball: { ...world.ball }, winner: world.winner };
}

function updateHud(): void {
  const local = world.paddles.get(localId);
  const rival = [...world.paddles.values()].find((paddle) => paddle.id !== localId);
  ui.setScore(local?.score ?? 0, rival?.score ?? 0);
  ui.setStartVisible(role !== 'idle' && !world.started && !world.winner && world.paddles.size === 2);
  ui.setResult(world.winner, localSide);
}

function resetSessionState(): void {
  world.paddles.clear();
  world.winner = null;
  world.tick = 0;
  world.hitCount = 0;
  world.started = false;
  Object.assign(world.ball, { x: 0, z: 0, vx: 0, vz: 0 });
  remoteInputs.clear();
  sequence = 0;
  localId = '';
  localSide = null;
  ui.setResult(null, null);
  ui.setStartVisible(false);
  ui.copyInviteButton.hidden = true;
  ui.shareInviteButton.hidden = true;
}

const invitedRoom = new URL(window.location.href).searchParams.get('room')?.trim();
if (invitedRoom) void joinRoom(invitedRoom);

animationFrame = requestAnimationFrame(frame);
window.addEventListener('pagehide', () => {
  cancelAnimationFrame(animationFrame);
  session.close();
  input.stop();
  renderer.dispose();
}, { once: true });
